import { useParams, Link, Navigate } from "react-router-dom";
import { useGenerationStatus, isGenerationAllowed } from "@/hooks/use-generation";
import { CheckCircle2, XCircle, ArrowLeft, ArrowRight, RotateCcw, Download, Loader2, Lock, FastForward, Zap, Crown, Film, Gauge } from "lucide-react";
import { IconStarBurst, IconCoin } from "@/components/ui/cartoon-icons";
import { VideoWithLoader } from "@/components/ui/video-loader";
import { calculateProgress, calculateTimeRemaining, formatTime, inferClipSeconds } from "@/lib/generation-utils";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useUserStore } from "@/stores/user-store";

const TIPS = [
  "Describe colors and expressions for more vivid results",
  "Try styles like 'Pixar', 'Disney', or 'Ghibli'",
  "Add accessories like hats, capes, or magic wands",
  "Mention poses and actions for more dynamic scenes",
  "Include background details for richer environments",
];

function OrbitDot({ delay, duration, radius, size, color }: { delay: number; duration: number; radius: number; size: number; color: string }) {
  return (
    <div
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{
        width: size,
        height: size,
        background: color,
        animation: `orbit ${duration}s linear ${delay}s infinite`,
        "--orbit-radius": `${radius}px`,
        boxShadow: `0 0 ${size * 2}px ${color}`,
      } as React.CSSProperties}
    />
  );
}

function ExpandingRing({ delay }: { delay: number }) {
  return (
    <div
      className="absolute inset-0 rounded-full border border-[#b8a5f0]/30"
      style={{
        animation: `ring-expand 3s ease-out ${delay}s infinite`,
      }}
    />
  );
}

function GrainOverlay({ opacity }: { opacity: number }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none z-10 mix-blend-overlay"
      style={{
        opacity,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
        backgroundSize: "128px 128px",
        animation: "grain 0.5s steps(6) infinite",
      }}
    />
  );
}

export function GeneratePage() {
  const { id } = useParams<{ id: string }>();
  const allowed = id ? isGenerationAllowed(id) : false;
  const { data } = useGenerationStatus(allowed ? (id ?? null) : null);
  const { hasPurchased, credits } = useUserStore();

  if (!id || !allowed) {
    return <Navigate to="/" replace />;
  }
  const generation = data?.generation;
  const [fallbackStart] = useState(() => Date.now());
  const [, setTick] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    if (generation?.status === "completed" || generation?.status === "failed") return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [generation?.status]);

  useEffect(() => {
    if (generation?.status === "completed" || generation?.status === "failed") return;
    const interval = setInterval(() => setTipIndex((i) => (i + 1) % TIPS.length), 4000);
    return () => clearInterval(interval);
  }, [generation?.status]);

  const clipSeconds = inferClipSeconds(
    generation?.model_id,
    generation?.credits_used,
    generation?.duration_ms,
  );
  const startTime = generation?.created_at
    ? new Date(generation.created_at).getTime()
    : fallbackStart;
  const progress = calculateProgress(startTime, generation?.model_id, clipSeconds);
  const timeRemaining = calculateTimeRemaining(startTime, generation?.model_id, clipSeconds);
  const grainOpacity = Math.max(0, 0.6 - (progress / 100) * 0.55);

  const MODEL_ROUTES: Record<string, string> = {
    qwen_image: "/create-character",
    qwen_image_edit: "/edit-character",
    cs_t2v_480p_5s: "/create-cartoon",
    cs_480p_i2v_5s: "/create-cartoon",
    cs_480p_i2v_8s: "/create-cartoon",
    cs_720p_i2v_5s: "/create-cartoon",
    cs_720p_i2v_8s: "/create-cartoon",
    wan25_720p_i2v_5s: "/create-cartoon",
    integrated_h3_i2v: "/create-cartoon",
  };
  const createRoute = MODEL_ROUTES[generation?.model_id || ""] || "/create-cartoon";

  const hasRefImage = !!generation?.reference_image;

  const promptWords = useMemo(() => {
    if (!generation?.prompt) return [];
    return generation.prompt.split(/\s+/).slice(0, 12);
  }, [generation?.prompt]);

  return (
    <div className="p-5 md:p-8 max-w-3xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#241d3a]/55 hover:text-[#241d3a] transition-colors mb-8 font-semibold">
        <ArrowLeft className="size-3.5" /> Home
      </Link>

      {(!generation || generation.status === "pending" || generation.status === "processing") && (
        <div className="flex flex-col items-center justify-center py-8 animate-fade-in-up">

          <div className="relative w-64 h-64 mb-8">
            {hasRefImage ? (
              <div className="absolute inset-0 rounded-3xl overflow-hidden">
                <img
                  src={generation.reference_image!}
                  alt=""
                  className="w-full h-full object-cover"
                  style={{
                    filter: `blur(${Math.max(0, 30 - (progress / 100) * 30)}px) saturate(${0.3 + (progress / 100) * 0.7}) brightness(${0.5 + (progress / 100) * 0.5})`,
                    opacity: 0.4 + (progress / 100) * 0.5,
                    transform: `scale(${1.12 - (progress / 100) * 0.12})`,
                    transition: "filter 2s ease-out, opacity 2s ease-out, transform 2s ease-out",
                  }}
                />
                <GrainOverlay opacity={grainOpacity} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#f3f0fb]/90 via-transparent to-[#f3f0fb]/40" />
              </div>
            ) : (
              <div className="absolute inset-4 overflow-hidden" style={{ animation: "morph-blob 8s ease-in-out infinite" }}>
                <div
                  className="absolute inset-0"
                  style={{
                    background: `radial-gradient(ellipse at 30% 40%, rgba(184,165,240,0.25), transparent 60%),
                                 radial-gradient(ellipse at 70% 60%, rgba(124,92,240,0.2), transparent 50%),
                                 radial-gradient(ellipse at 50% 50%, rgba(184,165,240,0.1), transparent 70%)`,
                    animation: "morph-blob 8s ease-in-out infinite",
                  }}
                />
                <GrainOverlay opacity={grainOpacity} />
              </div>
            )}

            <ExpandingRing delay={0} />
            <ExpandingRing delay={1} />
            <ExpandingRing delay={2} />

            <OrbitDot delay={0} duration={6} radius={90} size={4} color="rgba(184,165,240,0.8)" />
            <OrbitDot delay={0.5} duration={8} radius={100} size={3} color="rgba(184,165,240,0.5)" />
            <OrbitDot delay={1} duration={10} radius={110} size={2} color="rgba(184,165,240,0.3)" />
            <OrbitDot delay={0.3} duration={7} radius={95} size={3} color="rgba(124,92,240,0.6)" />
            <OrbitDot delay={1.5} duration={12} radius={115} size={2} color="rgba(36,29,58,0.2)" />

            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="size-14 rounded-full bg-white/80 backdrop-blur-md border border-[#241d3a]/10 flex items-center justify-center shadow-[0_0_40px_rgba(184,165,240,0.3)]">
                <IconStarBurst className="size-6 text-[#7c5cf0] animate-float" />
              </div>
            </div>
          </div>

          <h2 className="text-xl font-extrabold text-[#241d3a] mb-1.5 tracking-tight">Creating your generation~</h2>
          <p className="text-sm text-[#241d3a]/60 mb-2.5 tabular-nums">
            {timeRemaining !== null ? `~${formatTime(timeRemaining)} remaining` : "This usually takes about a minute..."}
          </p>

          {promptWords.length > 0 && (
            <div className="flex flex-wrap justify-center gap-1.5 mb-4 max-w-xs">
              {promptWords.map((word, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full text-[9px] font-bold border animate-fade-in"
                  style={{
                    animationDelay: `${i * 100}ms`,
                    color: `rgba(124,92,240,${0.4 + (progress / 100) * 0.5})`,
                    borderColor: `rgba(184,165,240,${0.1 + (progress / 100) * 0.15})`,
                    background: `rgba(184,165,240,${0.03 + (progress / 100) * 0.04})`,
                    transition: "color 2s, border-color 2s, background 2s",
                  }}
                >
                  {word}
                </span>
              ))}
            </div>
          )}

          <p className="text-[11px] text-[#7c5cf0]/70 mb-6 h-4 transition-opacity">
            <IconCoin className="size-2.5 inline mr-1 relative -top-px" />
            {TIPS[tipIndex]}
          </p>

          <div className="w-full max-w-xs">
            <div className="h-2 rounded-full bg-[#241d3a]/[0.06] overflow-hidden backdrop-blur-sm">
              <div
                className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                style={{
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, #7c5cf0, #b8a5f0, #7c5cf0)",
                  backgroundSize: "200% 100%",
                  animation: "gradient-shift 2s ease-in-out infinite",
                }}
              >
                <div
                  className="absolute inset-0 bg-white/30"
                  style={{ animation: "shimmer 1.5s ease-out infinite" }}
                />
              </div>
            </div>
            <div className="flex justify-between mt-2.5">
              <p className="text-[10px] text-[#241d3a]/55 tabular-nums font-bold">{Math.round(progress)}%</p>
              <p className="text-[10px] text-[#241d3a]/55 font-bold">{generation?.status === "pending" ? "Queued" : "Processing"}</p>
            </div>
          </div>

          {!hasPurchased && (
            <Link
              to="/billing"
              className="mt-10 w-full max-w-sm p-4 rounded-2xl bg-[#241d3a]/[0.04] border border-[#b8a5f0]/20 hover:border-[#b8a5f0]/40 transition-all group block"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="size-8 rounded-xl bg-[#b8a5f0]/10 border border-[#b8a5f0]/20 flex items-center justify-center">
                  <Crown className="size-4 text-[#7c5cf0]" />
                </div>
                <p className="text-[13px] font-bold text-[#241d3a]">Upgrade Your Creations</p>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center gap-1.5">
                  <Lock className="size-3 text-[#7c5cf0]/70" />
                  <span className="text-[11px] text-[#241d3a]/60">No watermarks</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Zap className="size-3 text-[#7c5cf0]/70" />
                  <span className="text-[11px] text-[#241d3a]/60">Better quality models</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Film className="size-3 text-[#7c5cf0]/70" />
                  <span className="text-[11px] text-[#241d3a]/60">Longer generations</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Gauge className="size-3 text-[#7c5cf0]/70" />
                  <span className="text-[11px] text-[#241d3a]/60">Faster generation times</span>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-[#b8a5f0]/10 border border-[#b8a5f0]/20 group-hover:bg-[#b8a5f0]/15 transition-colors">
                <IconStarBurst className="size-3.5" />
                <span className="text-[12px] font-bold text-[#7c5cf0]">Buy Tokens</span>
              </div>
            </Link>
          )}
        </div>
      )}

      {generation?.status === "completed" && generation.url && (() => {
        const isImage = generation.model_id === "qwen_image" || generation.model_id === "qwen_image_edit";
        // The API only sends the copy this user may see: watermarked without a purchase.
        const displayUrl = generation.url;
        // Only send a rerun back to the create form if the balance actually covers
        // it; otherwise the pack page is the honest destination.
        const canAffordRerun = credits > 0 && credits >= (generation.credits_used ?? 0);
        return (
        <div className="animate-fade-in-up">
          <div className="relative rounded-3xl overflow-hidden border border-[#241d3a]/15 mb-6 bg-[#f3f0fb] max-h-[70vh] flex items-center justify-center">
            {isImage ? (
              <img src={displayUrl} alt="Generation result" className="max-w-full max-h-[70vh] object-contain" style={{ animation: "diffuse-in 1.2s ease-out forwards" }} />
            ) : (
              <VideoWithLoader src={displayUrl} controls autoPlay loop playsInline wrapperClassName="flex items-center justify-center" className="max-w-full max-h-[70vh] object-contain" style={{ animation: "diffuse-in 1.2s ease-out forwards" }} />
            )}
            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/60 backdrop-blur-sm border border-white/20">
              <CheckCircle2 className="size-3 text-emerald-500" />
              <span className="text-[10px] font-bold text-emerald-600">Complete</span>
            </div>
          </div>

          {!isImage && (
            <Link
              to={hasPurchased ? `/extend-cartoon?video_id=${generation.id}` : "/billing?src=post_gen_extend"}
              className="mb-4 block w-full p-4 rounded-2xl bg-gradient-to-r from-[#7c5cf0]/20 to-[#b8a5f0]/10 border-2 border-[#7c5cf0]/40 hover:border-[#7c5cf0]/60 hover:from-[#7c5cf0]/25 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="size-11 rounded-2xl bg-[#7c5cf0]/15 border border-[#7c5cf0]/30 flex items-center justify-center shrink-0">
                  <FastForward className="size-5 text-[#7c5cf0]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-extrabold text-[#241d3a] tracking-tight">
                    🎬 Add 5–15 seconds more now
                  </p>
                  <p className="text-[12px] text-[#241d3a]/60 mt-0.5 leading-snug">
                    {hasPurchased
                      ? "Continue this scene where it left off — new motion, dialog, or a fresh beat."
                      : "Get tokens to extend this cartoon with more action, dialog, or a new scene."}
                  </p>
                </div>
                <ArrowRight className="size-4 text-[#7c5cf0] shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          )}

          {!hasPurchased ? (
            <>
              <p className="text-sm text-[#241d3a] text-center mb-4 leading-relaxed">
                Like this quality? Unlock <span className="font-semibold">Supercharged</span> and more generations with any token pack.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Link
                  to="/billing?src=post_gen_watermark"
                  className="btn-primary w-full py-3 rounded-xl flex items-center justify-center gap-2 text-[13px]"
                >
                  <Lock className="size-4" /> Remove Watermark
                </Link>
                <Link
                  to={canAffordRerun ? createRoute : "/billing?src=post_gen_regen"}
                  className="btn-glass w-full py-3 rounded-xl flex items-center justify-center gap-2 text-[13px] border-[#b8a5f0]/20 hover:border-[#b8a5f0]/40"
                >
                  <RotateCcw className="size-4 text-[#7c5cf0]" /> Generate Again
                </Link>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                <DownloadButton url={displayUrl} isImage={isImage} label="Download Preview" />
                <span className="text-[10px] text-[#241d3a]/55 inline-flex items-center gap-1"><IconCoin className="size-2.5" />{generation.credits_used} tokens used</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <Link to={createRoute} className="btn-primary flex items-center gap-2 text-[13px] py-2.5 px-5">
                <IconStarBurst className="size-3.5" /> Create Another
              </Link>
              <DownloadButton url={displayUrl} isImage={isImage} />
              <span className="text-[10px] text-[#241d3a]/55 inline-flex items-center gap-1"><IconCoin className="size-2.5" />{generation.credits_used} tokens used</span>
            </div>
          )}

          {hasPurchased && (
            <div className="mt-6 text-center">
              <p className="text-[13px] text-[#241d3a]/55">
                Unhappy with a generation? <a href="mailto:support@cartoontale.com" className="text-[#7c5cf0] font-semibold hover:underline">Send us an email</a> and we'll help you out :)
              </p>
            </div>
          )}

          {generation.credits_used === 0 && generation.model_id === "integrated_h3_i2v" && (
            <Link
              to="/create-cartoon"
              className="mt-4 block w-full p-4 rounded-2xl bg-[#b8a5f0]/15 border-2 border-[#7c5cf0]/20 hover:border-[#7c5cf0]/40 transition-all"
            >
              <p className="text-[15px] font-extrabold text-[#241d3a] tracking-tight mb-1">
                Like what you see?
              </p>
              <p className="text-[13px] text-[#241d3a]/60 leading-relaxed font-semibold">
                Create more videos like this with <span className="font-bold text-[#7c5cf0]">Supercharged</span> on the Generate Video page. Same upgraded pipeline, same quality.
              </p>
              <div className="flex items-center gap-2 mt-3 text-[13px] font-extrabold text-[#7c5cf0]">
                <IconStarBurst className="size-4" />
                Go to Generate Video
                <ArrowRight className="size-3.5" />
              </div>
            </Link>
          )}
        </div>
        );
      })()}

      {generation?.status === "completed" && generation.locked && (
        <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up">
          <div className="size-14 rounded-full bg-[#b8a5f0]/15 border border-[#241d3a]/15 flex items-center justify-center mb-4">
            <Lock className="size-6 text-[#7c5cf0]" />
          </div>
          <h2 className="text-base font-bold text-[#241d3a] mb-1">Your creation is ready</h2>
          <p className="text-xs text-[#241d3a]/60 mb-5 text-center max-w-sm">Get any token pack to view and download it.</p>
          <Link to="/billing?src=post_gen_locked" className="btn-primary inline-flex items-center gap-2 text-[13px]">
            <Lock className="size-3.5" /> Unlock with a token pack
          </Link>
        </div>
      )}

      {generation?.status === "failed" && (
        <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up">
          <div className="size-14 rounded-full bg-red-500/10 border border-[#241d3a]/15 flex items-center justify-center mb-4">
            <XCircle className="size-6 text-red-400" />
          </div>
          <h2 className="text-base font-bold text-[#241d3a] mb-1">Generation Failed</h2>
          <p className="text-xs text-[#241d3a]/60 mb-1 text-center max-w-sm">{generation.error_message || "Something went wrong"}</p>
          <p className="text-[11px] text-emerald-500 mb-5 inline-flex items-center gap-1"><IconCoin className="size-3" />Tokens have been refunded</p>
          <Link to={createRoute} className="btn-primary inline-flex items-center gap-2 text-[13px]">
            <RotateCcw className="size-3.5" /> Try Again
          </Link>
        </div>
      )}
    </div>
  );
}

function DownloadButton({ url, isImage, label = "Download" }: { url: string; isImage: boolean; label?: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const ext = isImage ? "png" : "mp4";
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `cartoontale-generation.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    } finally {
      setDownloading(false);
    }
  }, [url, isImage]);

  return (
    <button
      onClick={handleDownload}
      disabled={downloading}
      className="btn-glass flex items-center gap-2 text-[13px] py-2.5 px-5 rounded-xl"
    >
      {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
      {label}
    </button>
  );
}
